import Label from '@components/obsolete/Form/Label/Label';
import React, { useContext, useEffect, useState } from 'react';
import { ListUserDepartmentOutDTO, LovSecurityRolesDTO, UserCompanyUserInDTO } from '@api/ringil-api.v2';
import { isNullOrUndefined } from '@utils/utils';
import * as yup from 'yup';
import { ApiContext } from '@api/api';
import useForm from '@hooks/useForm/useForm';
import InputPickSecurityRole from '@components/obsolete/Form/InputPickSecurityRole/InputPickSecurityRole';
import InputPickSecurityGroup from '@components/obsolete/Form/InputPickSecurityGroup/InputPickSecurityGroup';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { RoleEnum } from '@type/enums.ts';
import InputYesNoPlain from '@components/obsolete/Form/InputYesNoPlain/InputYesNoPlain.tsx';
import EditableDropdown, { DropdownItem } from '@components/ringil3/EditableDropdown/EditableDropdown.tsx';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import RingilInputText from '@components/framework/input/RingilInput/RingilInputText.tsx';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';

interface ComponentProps {
  visible: boolean;
  onHide: () => void;
  onComplete: () => void;
  userId?: string;
  companyId: string;
  loadSecurityRoles?: (onSuccess: ((data: LovSecurityRolesDTO[]) => void)) => void;
  isAdmin?: boolean;
}

export const EditUserSidebar: React.FC<ComponentProps> = ({
                                                            visible,
                                                            onHide,
                                                            onComplete,
                                                            userId,
                                                            companyId,
                                                            loadSecurityRoles,
                                                            isAdmin = false,
                                                          }) => {
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const { tr } = useTranslationLgs();
  const {
    adminUpdateUser,
    getCompanyUserDetail,
    createUserUnderCompany,
    adminCreateUserUnderCompany,
    updateUserUnderCompany,
    listAvailableUserDepartmentsForCurrentCompany,
    deleteCompanyUserDepartment,
  } = useContext(ApiContext);
  const [departments, setDepartments] = useState<ListUserDepartmentOutDTO[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<DropdownItem[]>([]);
  const { setBeValidationErrors, BackendValidationComponent, findFieldError, clear: clearBeValidation } = useBEValidation();

  const doNotValidIfOnlyDriverRole = (isEmail: boolean) => {
    return (roles: any, schema: any) => {
      return roles.includes(RoleEnum.ROLE_DRIVER) && roles.length === 1
        ? schema //do nothing
        : isEmail
        ? yup.string().email().required()
        : yup.string().required(); //required
    };
  };

  const validationScheme = yup.object().shape({
    firstname: yup.string().defined().required(),
    email: yup.string().when('roles', doNotValidIfOnlyDriverRole(true)),
    phone: yup.string().when('roles', doNotValidIfOnlyDriverRole(false)),
    surname: yup.string().defined().required(),
  });

  const form = useForm<UserCompanyUserInDTO>(
    validationScheme,
    {
      firstname: '',
      email: '',
      phone: '',
      surname: '',
      roles: [],
      securityRolesIds: [],
      userId: null,
      securityGroupsIds: [],
      updateUserInAuth0: true,
      departmentName: '',
    } as UserCompanyUserInDTO,
    d => save(d),
    false,
    false,
  );

  useEffect(() => {
    if (visible) {
      form.clear();
      clearBeValidation();
      listAvailableUserDepartmentsForCurrentCompany(data => {
        setDepartments(data);
      });
      if (isNullOrUndefined(userId)) {
        form.setValues({
          firstname: '',
          email: '',
          phone: '',
          surname: '',
          roles: [],
          securityRolesIds: [],
          userId: null,
          securityGroupsIds: [],
          updateUserInAuth0: true,
          departmentName: '',
        } as UserCompanyUserInDTO);
      } else {
        getCompanyUserDetail(userId, data => {
          form.setValues({
            firstname: data.firstname,
            email: data.email,
            phone: data.phone,
            surname: data.surname,
            roles: data.roles,
            securityRolesIds: data.securityRoles.map(r => r.id),
            userId: data.userId,
            securityGroupsIds: data.securityGroups.map(r => r.id),
            updateUserInAuth0: true,
            departmentNames: data.departmentNames,
          } as UserCompanyUserInDTO);
        });
      }
    }
  }, [visible]);

  const getSelectedOptions = (departmentNames?: string[]) => {
    if (!departmentNames) return [];

    return departmentNames.map(name => {
      const existingDepartment = departments.find(department => department.name === name);
      if (existingDepartment) {
        return {
          id: existingDepartment.id,
          value: existingDepartment.name,
        };
      }

      return {
        id: name,
        value: name,
      };
    });
  };

  useEffect(() => {
    if (form.values?.departmentNames) {
      const departments = getSelectedOptions(form.values.departmentNames);
      setSelectedOptions(departments);
    }
  }, [form.values?.departmentNames, departments]);

  const save = (data: UserCompanyUserInDTO) => {
    const beValidationCallbacks = {
      onValidationFailed: (errors: any) => setBeValidationErrors(errors),
    };

    if (userId) {
      if (isAdmin) {
        adminUpdateUser(userId, form.values, () => {
          onComplete();
        }, null, beValidationCallbacks);
      } else {
        updateUserUnderCompany(userId, form.values, () => {
          onComplete();
        }, null, beValidationCallbacks);
      }
    } else {
      if (companyId != loggedUser.companyId) {
        adminCreateUserUnderCompany(companyId, form.values, () => {
          onComplete();
        }, null, beValidationCallbacks);
      } else {
        createUserUnderCompany(form.values, () => {
          onComplete();
        }, null, beValidationCallbacks);
      }
    }
  };

  const handleSelectDepartment = (items: DropdownItem[]) => {
    const uniqueItems = items.filter((item, index, self) =>
      index === self.findIndex(i => i.value.trim().toLowerCase() === item.value.trim().toLowerCase())
    );

    setSelectedOptions(uniqueItems);
    form.setFieldValue(form.names.departmentNames, uniqueItems.map(item => item.value));

  };

  const handleDropDownDelete = (item: DropdownItem) => {
    try {
      deleteCompanyUserDepartment(item.id, () => {
        listAvailableUserDepartmentsForCurrentCompany(data => {
          setDepartments(data);
        });
      });
    } catch (error) {
      console.error('Error in handleDropDownDelete:', error);
    }
  };

  return (
    <>
      <RingilSidebar
        heading={userId ? tr('EditUserSidebar.editUser', 'Edit user') : tr('EditUserSidebar.createUser', 'Create user')}
        visible={visible}
        onBack={onHide}
        onContinue={form.validateAndSend}
        maxWidth={'800px'}
      >
        <div className={'flex flex-col gap-2'}>
          <BackendValidationComponent />
          <Field
            label={tr('EditUserSidebar.firstName', 'First name')}
            errorMessage={form.errors.firstname ?? findFieldError('firstname')}
            required
          >
            <RingilInputText
              value={form?.values?.firstname}
              onChange={v => form.setFieldValue(form.names.firstname, v)}
              fullWidth
              />
          </Field>

          <Field
            label={tr('EditUserSidebar.surname', 'Surname')}
            errorMessage={form.errors.surname ?? findFieldError('surname')}
            required
          >
            <RingilInputText
              value={form?.values?.surname}
              onChange={v => form.setFieldValue(form.names.surname, v)}
              fullWidth
              />
          </Field>

          <Field
            label={tr('EditUserSidebar.email', 'Email')}
            errorMessage={form.errors.email ?? findFieldError('email')}
            required
          >
            <RingilInputText
              value={form?.values?.email}
              onChange={v => form.setFieldValue(form.names.email, v)}
              fullWidth
            />
          </Field>

          <Field
            label={tr('EditUserSidebar.phone', 'Phone')}
            errorMessage={form.errors.phone ?? findFieldError('phone')}
            required
          >
            <RingilInputText
              value={form?.values?.phone}
              onChange={v => form.setFieldValue(form.names.phone, v)}
              fullWidth
              />
          </Field>

          <div className={'flex flex-col gap-2'}>
            <div className="font-medium text-sm leading-3 text-content-primary">
              {tr('EditUserSidebar.companyDepartment', 'Department')}
            </div>
              <EditableDropdown
                options={departments.map(department => ({ id: department.id, value: department.name }))}
                selectedOptions={selectedOptions}
                onSelect={(items: DropdownItem[]) => handleSelectDepartment(items)}
                onDelete={(item: DropdownItem) => handleDropDownDelete(item)}
                placeholder={tr('EditUserSidebar.companyDepartmentPlaceholder', 'Please select department')}
                multiSelect
              />
            </div>

            <div className="p-field p-fluid">
              <Label title={`${tr('EditUserSidebar.securityRoles', 'Security roles')}`} />
              <InputPickSecurityRole
                value={form.values.securityRolesIds}
                onChange={v => form.setFieldValue(form.names.securityRolesIds, v)}
                loadSecurityRoles={loadSecurityRoles}
                isAdmin={isAdmin}
              />
            </div>
            <div className="p-field p-fluid">
              <Label title={`${tr('EditUserSidebar.securityGroups', 'Security groups')}`} />
              <InputPickSecurityGroup
                value={form.values.securityGroupsIds}
                onChange={v =>
                  form.setFieldValue(
                    form.names.securityGroupsIds,
                    v.map(t => t.id),
                  )
                }
              />
            </div>
            <div className="p-field p-fluid">
              <Label title={`${tr('EditUserSidebar.updateInAuth0', 'Update external user')}`} />
              <InputYesNoPlain value={form.values.updateUserInAuth0}
                               onChange={v => form.setFieldValue(form.names.updateUserInAuth0, v)} />
            </div>
        </div>
      </RingilSidebar>
    </>
  );
};
